import { z } from 'zod';
import type { Role } from '../permissions/types.js';
import { failure, success, type Result } from '../types.js';
import { LEGACY_SPOT_CASH_RANGE, type SpotCashRange } from './policy.js';

export const areteValues = [
  'Creativity',
  'Integrity',
  'Passion',
  'Responsiveness',
  'Collaboration',
] as const;
const suggestedAmountSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
/** APP-240 (change 1): the Title is the letter's line ("…in recognition of {title}."), so short. */
export const SPOT_TITLE_MAX = 80;
const titleSchema = z.string().trim().max(SPOT_TITLE_MAX);
/**
 * A draft keeps what the nominator has typed so far. `writeup` is the Description (APP-240 change
 * 2): renamed on screen and in the contract, never in storage. A blank title is no title yet.
 * Every nomination is a surprise (Q27), so there's no choice to carry.
 */
const draftSchema = z
  .object({
    nominatorId: z.string().trim().min(1),
    nomineeIds: z.array(z.string().trim().min(1)).max(50),
    title: titleSchema.nullable().transform((title) => (title === '' ? null : title)),
    writeup: z.string().trim().max(10000),
    value: z.enum(areteValues).nullable(),
    suggestedAmountCents: suggestedAmountSchema.nullable(),
  })
  .strict();
/** On send: colleagues, a title and a description. The Areté value stays optional (Q9). */
const nominationSchema = draftSchema.extend({
  nomineeIds: draftSchema.shape.nomineeIds.min(1),
  title: titleSchema.min(1),
  writeup: draftSchema.shape.writeup.min(1),
  suggestedAmountCents: suggestedAmountSchema.optional(),
});

export type SpotDraft = z.infer<typeof draftSchema>;
export type SpotNomination = z.infer<typeof nominationSchema>;

function validateRecipients<T extends Pick<SpotDraft, 'nominatorId' | 'nomineeIds'>>(
  nomination: T,
): Result<T> {
  if (new Set(nomination.nomineeIds).size !== nomination.nomineeIds.length)
    return failure('duplicate_recipient');
  return success(nomination);
}

export function validateSpotDraft(
  input: unknown,
  range: SpotCashRange = LEGACY_SPOT_CASH_RANGE,
): Result<SpotDraft> {
  const parsed = draftSchema.safeParse(input);
  if (!parsed.success)
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => ({
        code: 'invalid_spot_draft',
        field: issue.path.map(String).join('.'),
      })),
    };
  if (
    parsed.data.suggestedAmountCents !== null &&
    (parsed.data.suggestedAmountCents < range.minCents ||
      parsed.data.suggestedAmountCents > range.maxCents)
  )
    return failure('invalid_spot_draft', 'suggestedAmountCents');
  return validateRecipients(parsed.data);
}

export function validateNomination(
  input: unknown,
  range: SpotCashRange = LEGACY_SPOT_CASH_RANGE,
): Result<SpotNomination> {
  const parsed = nominationSchema.safeParse(input);
  if (!parsed.success)
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => ({
        code: 'invalid_nomination',
        field: issue.path.map(String).join('.'),
      })),
    };
  if (
    parsed.data.suggestedAmountCents !== undefined &&
    (parsed.data.suggestedAmountCents < range.minCents ||
      parsed.data.suggestedAmountCents > range.maxCents)
  )
    return failure('invalid_nomination', 'suggestedAmountCents');
  return validateRecipients(parsed.data);
}
export type SpotState = 'draft' | 'sent' | 'awarded' | 'not_awarded';
export interface SpotTransitionContext {
  readonly actorId: string;
  readonly roles: readonly Role[];
  readonly reviewerIds: readonly string[];
  readonly previewing: boolean;
  readonly nomination: SpotNomination;
  readonly cashRange?: SpotCashRange;
  readonly awardId?: string;
}
/** Current reviewer IDs are resolved centrally, including legacy grants before explicit setup.
 * Use final transitions only after the saved route's approval and financial prerequisites
 * succeed in the same audited transaction. This pure helper cannot establish those facts. */
export function transitionSpot(
  state: SpotState,
  event: 'submit' | 'award' | 'decline',
  ctx: SpotTransitionContext,
): Result<{ readonly next: SpotState; readonly awardId?: string }> {
  if (ctx.previewing) return failure('preview_read_only');
  const valid = validateNomination(ctx.nomination, ctx.cashRange);
  if (!valid.ok) return valid;
  if (state === 'draft' && event === 'submit')
    return ctx.actorId === valid.value.nominatorId
      ? success({ next: 'sent' })
      : failure('not_authorized');
  if (state !== 'sent' || event === 'submit') return failure('invalid_transition');
  if (!ctx.reviewerIds.includes(ctx.actorId)) return failure('not_authorized');
  if (event === 'decline') return success({ next: 'not_awarded' });
  if (!ctx.awardId?.trim()) return failure('linked_award_required');
  return success({ next: 'awarded', awardId: ctx.awardId });
}
export { effectiveSpotSupervisor, spotParentStatus, type SpotReviewStage } from './review.js';
export * from './policy.js';
export * from './queue.js';
export * from './visibility.js';
